import { useState, useEffect, useRef } from 'react';
import { aquaApi } from './aquaApi';

const CODE_EMOJI = {
  113:'☀️', 116:'⛅', 119:'☁️', 122:'☁️', 143:'🌫️',
  176:'🌦️', 179:'🌨️', 182:'🌨️', 185:'🌧️', 200:'⛈️',
  227:'❄️', 230:'❄️', 248:'🌫️', 260:'🌫️', 263:'🌦️',
  266:'🌦️', 281:'🌧️', 284:'🌧️', 293:'🌦️', 296:'🌦️',
  299:'🌧️', 302:'🌧️', 305:'🌧️', 308:'🌧️', 311:'🌨️',
  314:'🌨️', 317:'🌨️', 320:'🌨️', 323:'❄️', 326:'❄️',
  329:'❄️', 332:'❄️', 335:'❄️', 338:'❄️', 350:'🌧️',
  353:'🌦️', 356:'🌧️', 359:'🌧️', 362:'🌨️', 365:'🌨️',
  368:'❄️', 371:'❄️', 374:'🌨️', 377:'🌨️', 386:'⛈️',
  389:'⛈️', 392:'⛈️', 395:'❄️',
};

function emoji(code) { return CODE_EMOJI[code] || '🌡️'; }

const CACHE_TTL = 30 * 60 * 1000; // 30 min

export default function WeatherWidget({ theme, compact = false }) {
  const [location, setLocation]   = useState(null);   // null = not yet loaded
  const [weather,  setWeather]    = useState(null);
  const [loading,  setLoading]    = useState(false);
  const [editing,  setEditing]    = useState(false);
  const [input,    setInput]      = useState('');
  const [saveErr,  setSaveErr]    = useState('');
  const inputRef = useRef(null);

  const isDark = theme === 'dark';
  const pill   = { borderRadius: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 13px', border: 'none' };
  const ghost  = { ...pill, background: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.06)', color: isDark ? '#c8d8e8' : '#444', border: `1px solid ${isDark ? 'rgba(255,255,255,.15)' : 'rgba(0,0,0,.12)'}` };

  // Load saved location on mount
  useEffect(() => {
    aquaApi.getSettings()
      .then(s => {
        const loc = s?.weatherLocation || '';
        setLocation(loc);
        if (loc) fetchWeather(loc);
      })
      .catch(() => setLocation(''));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 50);
  }, [editing]);

  const fetchWeather = async (loc) => {
    const key    = `wx_${loc.toLowerCase()}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) { setWeather(data); return; }
      } catch (_) {}
    }
    setLoading(true);
    try {
      const res  = await fetch(`https://wttr.in/${encodeURIComponent(loc)}?format=j1`);
      if (!res.ok) throw new Error();
      const json = await res.json();
      const cc   = json.current_condition[0];
      const data = {
        temp:  cc.temp_C,
        feels: cc.FeelsLikeC,
        desc:  cc.weatherDesc[0].value,
        code:  parseInt(cc.weatherCode),
        hum:   cc.humidity,
        wind:  cc.windspeedKmph,
      };
      setWeather(data);
      localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch (_) {
      setWeather(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const loc = input.trim();
    if (!loc) return;
    setSaveErr('');
    try {
      await aquaApi.saveSetting({ key: 'weatherLocation', value: loc });
      setLocation(loc);
      setEditing(false);
      setWeather(null);
      fetchWeather(loc);
    } catch (_) {
      setSaveErr('Could not save — please try again');
    }
  };

  // Still loading initial setting
  if (location === null) return null;

  // Edit / set location form
  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="City, e.g. Vijayawada"
          style={{ width: compact ? 130 : 170, padding: '5px 12px', borderRadius: 20, fontSize: 12,
            border: `1px solid ${isDark ? 'rgba(255,255,255,.25)' : '#ccc'}`,
            background: isDark ? 'rgba(255,255,255,.08)' : '#fff',
            color: isDark ? '#e2e8f0' : '#202124', outline: 'none' }}
        />
        <button onClick={handleSave}
          style={{ ...pill, background: '#0ea5e9', color: '#fff', fontWeight: 700 }}>
          Save
        </button>
        {location && (
          <button onClick={() => setEditing(false)} style={{ ...ghost }}>✕</button>
        )}
        {saveErr && <span style={{ fontSize: 11, color: '#f87171' }}>{saveErr}</span>}
      </div>
    );
  }

  // No location set yet
  if (!location) {
    return (
      <button onClick={() => { setEditing(true); setInput(''); }} style={{ ...ghost }}>
        📍 Set weather location
      </button>
    );
  }

  // Show weather (or loading/error)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {loading && (
        <span style={{ ...ghost, cursor: 'default' }}>⏳ Loading weather…</span>
      )}

      {!loading && weather && (
        <button
          onClick={() => { setEditing(true); setInput(location); }}
          title={`${weather.desc} · Feels like ${weather.feels}°C · Humidity ${weather.hum}% · Wind ${weather.wind} km/h\nClick to change location`}
          style={{ ...ghost, gap: compact ? 4 : 8, padding: compact ? '5px 8px' : '5px 14px' }}>
          <span style={{ fontSize: compact ? 16 : 20, lineHeight: 1 }}>{emoji(weather.code)}</span>
          {compact ? (
            <span style={{ fontWeight: 700, fontSize: 13, color: isDark ? '#fff' : '#202124' }}>
              {weather.temp}°C
            </span>
          ) : (
            <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: isDark ? '#fff' : '#202124' }}>
                {weather.temp}°C
              </div>
              <div style={{ fontSize: 10, color: isDark ? '#94b4c8' : '#666' }}>
                {location} · {weather.desc}
              </div>
            </div>
          )}
        </button>
      )}

      {!loading && !weather && (
        <button onClick={() => { setEditing(true); setInput(location); }} style={{ ...ghost }}>
          🌡️ {location} — retry
        </button>
      )}
    </div>
  );
}
